import { Box, Flex } from '@chakra-ui/react';
import { Text } from '@kula-ai/components';
import { MarkerPin01 } from '@kula-ai/icons-core';
import { fillIdPlaceholders } from '../../../helpers';
import { useChatSubmit } from '../../../hooks';
import type { DashboardBlockData } from '../../../types';
import { SuggestedActionChips } from '../SuggestedActionChips';
import { Section } from './Section';

interface DashboardBlockProps {
  data: DashboardBlockData;
}

// Dashboards whose subtitle is a place — they get the location pin. Other
// dashboards render the subtitle plain.
const LOCATION_DASHBOARDS = new Set(['market_map', 'salary_benchmark']);

// The generic dashboard renderer: a backend-authored page spec drawn
// section-by-section. SCOPE: only the market-map and salary-benchmark skills
// emit this envelope — everything else keeps its bespoke block.
export const DashboardBlock = ({ data }: DashboardBlockProps) => {
  const { send, isSubmitting } = useChatSubmit();
  if (!data.sections?.length) return null;

  const sendFollowup = (template: string) => {
    if (isSubmitting) return;
    void send(fillIdPlaceholders(template, []), undefined, {
      selectionSnapshot: [],
    });
  };

  return (
    <Flex
      direction="column"
      gap="14px"
      w="100%"
      border="1px solid"
      borderColor="border-color"
      borderRadius="12px"
      bg="base-white"
      px="16px"
      py="14px"
      data-test-id={`dashboard-block-${data.dashboard}`}
    >
      <Box>
        <Text
          variant="span"
          className="text-[16px] text-secondary-900 font-semibold"
        >
          {data.title}
        </Text>
        {data.subtitle && (
          <Flex align="center" gap="4px" color="secondary-500" mt="2px">
            {LOCATION_DASHBOARDS.has(data.dashboard) && (
              <MarkerPin01 size={14} color="currentColor" />
            )}
            <Text className="text-[14px] text-secondary-500">
              {data.subtitle}
            </Text>
          </Flex>
        )}
      </Box>
      <Flex direction="column" gap="24px" mt="20px">
        {data.sections.map((section, i) => (
          <Section
            key={`${section.kind}-${section.title ?? i}`}
            section={section}
          />
        ))}
      </Flex>
      {data.suggestedActions && data.suggestedActions.length > 0 && (
        <SuggestedActionChips
          actions={data.suggestedActions}
          onSelect={sendFollowup}
          disabled={isSubmitting}
          testIdPrefix="dashboard-suggested-action"
        />
      )}
    </Flex>
  );
};
